// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, ExternalLink, Clock } from 'lucide-react';
import { getBlogArticle } from '@/lib/api';
import { useSEO } from '@/hooks/useSEO';
import DOMPurify from 'dompurify';

interface ContentBlock {
  type: string;
  content?: string;
  url?: string;
  caption?: string;
}

interface BlogArticleData {
  id: string;
  title: string;
  preview: string;
  content: ContentBlock[] | string; // Can be array (old format) or HTML string (new format)
  tags: string[];
  featured_image_url: string | null;
  featured_image_alt: string | null;
  author: string;
  created_at: string;
  notion_url?: string;
}

const BlogArticle = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState<BlogArticleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dynamic SEO for blog articles
  useSEO({
    title: article ? `${article.title} | Blog` : 'Blog-Artikel laden...',
    description: article?.preview || 'Lesen Sie unseren Blog-Artikel auf Klartech Müritz.',
    keywords: article?.tags?.join(', ') || 'IT Blog, Technik Tipps',
    canonical: `https://klartech-mueritz.de/blog/${articleId}`,
    ogType: 'article',
    ogImage: article?.featured_image_url || 'https://klartech-mueritz.de/og-image.jpg',
    structuredData: article ? {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.preview,
      image: article.featured_image_url,
      author: {
        '@type': 'Organization',
        name: article.author || 'Klartech Müritz',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Klartech Müritz',
        logo: {
          '@type': 'ImageObject',
          url: 'https://klartech-mueritz.de/images/logo-klartech.png',
        },
      },
      datePublished: article.created_at,
      mainEntityOfPage: `https://klartech-mueritz.de/blog/${articleId}`,
    } : undefined,
  });

  useEffect(() => {
    loadArticle();
  }, [articleId]);

  const loadArticle = async () => {
    if (!articleId) {
      setError('Keine Artikel-ID angegeben');
      setLoading(false);
      return;
    }

    try {
      const data = await getBlogArticle(articleId);
      if (data) {
        setArticle(data);
      } else {
        setError('Artikel nicht gefunden');
      }
    } catch (err) {
      console.error('Error loading article:', err);
      setError('Artikel konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const estimateReadingTime = (content: ContentBlock[] | string) => {
    const wordsPerMinute = 200;
    let words = 0;
    
    if (typeof content === 'string') {
      // HTML content - strip tags and count words
      const textOnly = content.replace(/<[^>]*>/g, ' ');
      words = textOnly.split(/\s+/).filter(w => w.length > 0).length;
    } else if (Array.isArray(content)) {
      // Block content
      words = content.reduce((acc, block) => {
        return acc + (block.content ? block.content.split(' ').length : 0);
      }, 0);
    }
    
    return Math.max(1, Math.ceil(words / wordsPerMinute));
  };

  // Check if content is HTML string
  const isHtmlContent = (content: ContentBlock[] | string): content is string => {
    return typeof content === 'string';
  };

  // Sanitize and render HTML content
  const renderHtmlContent = (html: string) => {
    const sanitizedHtml = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['h2', 'h3', 'p', 'ul', 'ol', 'li', 'strong', 'em', 'blockquote', 'br'],
      ALLOWED_ATTR: []
    });
    
    return (
      <div 
        className="blog-content"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    );
  };

  // Check if content contains HTML tags
  const containsHtml = (text: string | undefined): boolean => {
    if (!text) return false;
    return /<[a-z][\s\S]*>/i.test(text);
  };

  // Render content that may contain HTML (from AI generation)
  const renderBlockContentWithHtml = (text: string | undefined): React.ReactNode => {
    if (!text) return null;
    
    // If content contains HTML, render it directly with sanitization
    if (containsHtml(text)) {
      const sanitizedHtml = DOMPurify.sanitize(text, {
        ALLOWED_TAGS: ['h2', 'h3', 'p', 'ul', 'ol', 'li', 'strong', 'em', 'blockquote', 'br', 'code', 'a'],
        ALLOWED_ATTR: ['href', 'target', 'rel']
      });
      return <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
    }
    
    // Otherwise, parse inline markdown
    return parseInlineMarkdown(text);
  };

  // Parse inline markdown like **bold** and *italic* (for legacy block content)
  const parseInlineMarkdown = (text: string | undefined): React.ReactNode => {
    if (!text) return null;
    
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match;
    
    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      parts.push(<strong key={`bold-${match.index}`} className="font-semibold">{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  const renderContentBlock = (block: ContentBlock, index: number) => {
    const key = `block-${index}`;

    switch (block.type) {
      case 'h1':
        return (
          <h1 key={key} className="text-4xl font-bold text-slate-900 mb-6 mt-8 leading-tight">
            {renderBlockContentWithHtml(block.content)}
          </h1>
        );

      case 'h2':
        return (
          <h2 key={key} className="text-3xl font-bold text-slate-900 mb-4 mt-8 leading-tight">
            {renderBlockContentWithHtml(block.content)}
          </h2>
        );

      case 'h3':
        return (
          <h3 key={key} className="text-2xl font-bold text-slate-900 mb-3 mt-6 leading-tight">
            {renderBlockContentWithHtml(block.content)}
          </h3>
        );

      case 'paragraph':
        return (
          <div key={key} className="text-lg text-slate-700 leading-relaxed mb-4 blog-block-content">
            {renderBlockContentWithHtml(block.content)}
          </div>
        );
      
      case 'image':
        return (
          <div key={key} className="my-8">
            <img 
              src={block.url} 
              alt={block.caption || 'Artikel-Bild'}
              className="w-full rounded-xl shadow-lg"
            />
            {block.caption && (
              <p className="text-sm text-slate-500 text-center mt-2 italic">
                {block.caption}
              </p>
            )}
          </div>
        );

      case 'bullet':
        return (
          <li key={key} className="text-lg text-slate-700 leading-relaxed mb-2 ml-6 list-disc">
            {renderBlockContentWithHtml(block.content)}
          </li>
        );

      case 'number':
        return (
          <li key={key} className="text-lg text-slate-700 leading-relaxed mb-2 ml-6 list-decimal">
            {renderBlockContentWithHtml(block.content)}
          </li>
        );

      default:
        return null;
    }
  };

  const groupListItems = (content: ContentBlock[]) => {
    const grouped: any[] = [];
    let currentList: any = null;

    content.forEach((block, index) => {
      if (block.type === 'bullet') {
        if (!currentList || currentList.type !== 'bullet') {
          currentList = { type: 'bullet', items: [] };
          grouped.push(currentList);
        }
        currentList.items.push({ ...block, index });
      } else if (block.type === 'number') {
        if (!currentList || currentList.type !== 'number') {
          currentList = { type: 'number', items: [] };
          grouped.push(currentList);
        }
        currentList.items.push({ ...block, index });
      } else {
        currentList = null;
        grouped.push(block);
      }
    });

    return grouped;
  };

  const renderBlockContent = (content: ContentBlock[]) => {
    const grouped = groupListItems(content);

    return grouped.map((item, index) => {
      if (item.type === 'bullet' && item.items) {
        return (
          <ul key={`list-${index}`} className="mb-6 space-y-2">
            {item.items.map((listItem: any) => renderContentBlock(listItem, listItem.index))}
          </ul>
        );
      } else if (item.type === 'number' && item.items) {
        return (
          <ol key={`list-${index}`} className="mb-6 space-y-2">
            {item.items.map((listItem: any) => renderContentBlock(listItem, listItem.index))}
          </ol>
        );
      } else {
        return renderContentBlock(item, index);
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-slate-600 font-medium">Lade Artikel...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Artikel nicht gefunden</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link
            to="/blog"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  const content = article.content;
  const readingTime = estimateReadingTime(content);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header with Logo */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60 shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="relative">
              <img 
                src="/images/logo-klartech.png" 
                alt="Klartech Müritz Logo" 
                className="h-12 w-auto object-contain transition-transform hover:scale-105"
              />
              <div className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-green-500 animate-pulse ring-2 ring-white"></div>
            </div>
          </Link>
          <Link
            to="/blog"
            className="inline-flex items-center text-sm text-slate-600 hover:text-blue-600 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Alle Artikel
          </Link>
        </div>
      </header>

      {/* Article Content */}
      <article className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8 transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zur Übersicht
          </button>

          {/* Featured Image (Hero) */}
          {article.featured_image_url ? (
            <div className="relative overflow-hidden rounded-2xl mb-8 shadow-xl" style={{ maxHeight: '400px' }}>
              <img 
                src={article.featured_image_url} 
                alt={article.featured_image_alt || article.title}
                loading="lazy"
                className="w-full h-full object-cover"
                style={{ maxHeight: '400px' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            </div>
          ) : (
            /* Gradient Fallback */
            <div className="relative rounded-2xl mb-8 shadow-xl bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 flex items-center justify-center" style={{ height: '400px' }}>
              <div className="text-white text-center">
                <div className="text-8xl mb-4 opacity-50">📰</div>
                <p className="text-xl font-medium opacity-75">Klartech Blog</p>
              </div>
            </div>
          )}

          {/* Article Header */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-8">

            <div className="p-8 md:p-12">
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {(article.tags || []).map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </span>
                ))}
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                {article.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-slate-600 text-sm">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  {formatDate(article.created_at)}
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  {readingTime} Min. Lesezeit
                </div>
              </div>
            </div>
          </div>

          {/* Article Body */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 md:p-12">
            <div className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-li:text-slate-700 prose-strong:text-slate-900 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:p-4 prose-blockquote:rounded-lg">
              {content && isHtmlContent(content) ? (
                renderHtmlContent(content)
              ) : Array.isArray(content) && content.length > 0 ? (
                renderBlockContent(content)
              ) : (
                <p className="text-slate-600">{article.preview}</p>
              )}
            </div>
          </div>

          {/* Notion Link (optional) */}
          {article.notion_url && (
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-slate-50 rounded-2xl border border-blue-200">
              <a
                href={article.notion_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-700 hover:text-blue-800 font-medium transition-colors"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Artikel in Notion öffnen
              </a>
            </div>
          )}

          {/* Back to Overview */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <Link
              to="/blog"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-xl transition-all font-medium"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Zurück zur Übersicht
            </Link>
          </div>
        </div>
      </article>

      {/* CSS for blog content */}
      <style>{`
        .blog-content h2 {
          font-size: 1.875rem;
          font-weight: 700;
          color: rgb(15 23 42);
          margin-top: 2rem;
          margin-bottom: 1rem;
          line-height: 1.3;
        }
        .blog-content h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: rgb(15 23 42);
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          line-height: 1.4;
        }
        .blog-content p {
          font-size: 1.125rem;
          color: rgb(51 65 85);
          line-height: 1.75;
          margin-bottom: 1rem;
        }
        .blog-content ul, .blog-content ol {
          margin-top: 1rem;
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        .blog-content ul {
          list-style-type: disc;
        }
        .blog-content ol {
          list-style-type: decimal;
        }
        .blog-content li {
          font-size: 1.125rem;
          color: rgb(51 65 85);
          line-height: 1.75;
          margin-bottom: 0.5rem;
        }
        .blog-content strong {
          font-weight: 600;
          color: rgb(15 23 42);
        }
        .blog-content em {
          font-style: italic;
        }
        .blog-content blockquote {
          border-left: 4px solid rgb(59 130 246);
          background-color: rgb(239 246 255);
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          border-radius: 0.5rem;
          font-style: italic;
        }
        .blog-content blockquote p {
          margin-bottom: 0;
        }
        /* Styles for block content with embedded HTML */
        .blog-block-content h2 {
          font-size: 1.875rem;
          font-weight: 700;
          color: rgb(15 23 42);
          margin-top: 2rem;
          margin-bottom: 1rem;
          line-height: 1.3;
        }
        .blog-block-content h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: rgb(15 23 42);
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          line-height: 1.4;
        }
        .blog-block-content p {
          font-size: 1.125rem;
          color: rgb(51 65 85);
          line-height: 1.75;
          margin-bottom: 1rem;
        }
        .blog-block-content p:last-child {
          margin-bottom: 0;
        }
        .blog-block-content strong {
          font-weight: 600;
          color: rgb(15 23 42);
        }
        .blog-block-content em {
          font-style: italic;
        }
        .blog-block-content code {
          background-color: rgb(241 245 249);
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 0.9em;
          color: rgb(30 64 175);
        }
        .blog-block-content a {
          color: rgb(37 99 235);
          text-decoration: underline;
        }
        .blog-block-content a:hover {
          color: rgb(29 78 216);
        }
      `}</style>
    </div>
  );
};

export default BlogArticle;
